import * as React from "react";
import { Button, notification, Card } from "antd";
import { v4 as uuidv4 } from "uuid";
import {
  formInitialize,
  formSet,
  formUninitialize,
  useCpFormAnyValueId,
  useCpFormSubmit,
} from "../../gvc/testNewForm/utils";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { getDefaultSecretInput, inputToSecret, SecretTypeOptions } from "../utils";
import { SecretInput } from "../../../schema/types/secret/input";
import { Secret, SecretType } from "../../../schema/types/secret/entity";
import { getValidator } from "./validator";
import { homeLink, request, resourceLink } from "../../../services/cpln";
import { IS_DEPLOYMENT_ENV_PROD } from "../../../envVariables";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { Data } from "../data/data";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpInput } from "../../gvc/testNewForm/CpInput";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { CpInputDescription } from "../../gvc/testNewForm/CpInputDescription";
import { CpSelect } from "../../gvc/testNewForm/CpSelect";
import { CpErrorWarningArrayIcon } from "../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpLabelTooltip } from "../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../gvc/testNewForm/CpTooltip";

interface EmbeddedSecretCreateProps {
  onDone: (itemLink: string, item: Secret | undefined) => void | Promise<void>;
  onCancel: () => void;
  lockedType?: SecretType;
}

export const EmbeddedSecretCreate: React.FC<EmbeddedSecretCreateProps> = ({ onDone, onCancel, lockedType }) => {
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    const input = getDefaultSecretInput();
    if (lockedType) {
      input.type = lockedType;
    }
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  const submitting = useCpFormAnyValueId(formId, "meta.submitting");

  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;

  const submit = useCpFormSubmit({
    id: formId,
    validationFn: validator,
    onSubmit: handleSubmit,
  });

  async function handleSubmit(input: SecretInput) {
    try {
      const body = inputToSecret(input);
      await request({
        method: "post",
        url: homeLink("secret"),
        body,
      });
      const itemLink = resourceLink("secret", body.name);
      notification.success({
        message: "Success",
        description: "Secret is created",
      });
      let createdSecret: Secret | undefined;
      try {
        const { data } = await request({ url: itemLink });
        createdSecret = data;
      } catch (e) {}
      onDone(itemLink, createdSecret);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit }}>
      <CpFormValidator validationFn={validator} />
      <div className="flex flex-col gap-4">
        <div className="cpln-form-section cpln-ant-card-container">
          <Card>
            <CardTitle>
              Secret
              <CpErrorWarningArrayIcon names={["name", "description"]} />
            </CardTitle>
            <CardContent>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name="name">Name</CpLabel>
                  <CpTooltip tooltipProps={{ title: "Cannot be changed after creation (ok, my-item, item1)" }} />
                </CpLabelTooltip>
                <CpInput name="name" style={{ maxWidth: 600 }} />
                <CpErrorWarning name="name" />
              </CpFormItem>
              <CpInputDescription namePath="name" descriptionPath="description" />
              {lockedType ? (
                <CpFormItem>
                  <CpLabel name="type">Type</CpLabel>
                  <div className="text-sm">
                    {SecretTypeOptions.find((o) => o.value === lockedType)?.label || lockedType}
                  </div>
                </CpFormItem>
              ) : (
                <CpFormItem>
                  <CpLabelTooltip>
                    <CpLabel name="type">Type</CpLabel>
                    <CpTooltip
                      tooltipProps={{
                        title: `Select one of the supported secret types. If there is no fit for your data, Dictionary or Opaque is a more general choice.`,
                      }}
                    />
                  </CpLabelTooltip>
                  <CpSelect name="type" options={SecretTypeOptions} style={{ maxWidth: 600 }} />
                  <CpErrorWarning name="type" />
                </CpFormItem>
              )}
            </CardContent>
          </Card>
        </div>
        <Data />
        <CpTagsNew />
        <div className="flex flex-col gap-2">
          <Button type="primary" danger onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          {!IS_DEPLOYMENT_ENV_PROD && (
            <Button type="default" onClick={() => formSet(formId, "meta.debugging", true)}>
              Debug
            </Button>
          )}
          <Button type="primary" className="action" onClick={submit} loading={submitting} disabled={submitting}>
            Create
          </Button>
        </div>
      </div>
      <CpDebugModal inputToItemFn={inputToSecret} kind="secret" namePath="name" title="Secret" />
    </CpFormContext.Provider>
  );
};
